import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import left from "../../../assets/icons/Arrow/left.svg";
import Kakao from "../../../assets/icons/Channel/kakao_mini.svg";
import Feed from "../../../assets/icons/Channel/feed_mini.svg";
import Story from "../../../assets/icons/Channel/story_mini.svg";
import Band from "../../../assets/icons/Channel/band_mini.svg";
import X from "../../../assets/icons/Channel/x_mini.svg"
import MMS from "../../../assets/icons/Channel/mms_mini.svg"
import useTextExtraction from "../../../hooks/useTextExtraction";
import { SplitRowHalf, FillButton, TwinColumn } from "../../../assets/components/ButtonGroup";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import retry from "../../../assets/icons/System/repeat_inversed.svg";
import { addAppMessageListener, postToApp } from "../../../bridges/appBridge";
import platformFromChannel from "../../../hooks/getPlatform";
import useFormattedDateOnce from "../../../hooks/useFormattedDate";
import Popup from "../../../assets/components/PopupGroup";
import TokenPopup from "../../Ads/components/detailComponents/TokenPopup";
import checkOn from "../../../assets/icons/Select/check_checked.svg";
import checkOff from "../../../assets/icons/Select/check_unchecked.svg";

const channelMap = {
  1: "카카오톡",
  2: "인스타 스토리",
  3: "인스타 게시물",
  5: "MMS(문자)",
  6: "네이버 밴드",
  7: "X(트위터)"
};

const channelIconMap = {
  "카카오톡": Kakao,
  "인스타 스토리": Story,
  "인스타 게시물": Feed,
  "MMS(문자)": MMS, 
  "네이버 밴드": Band,
  "X(트위터)": X,
};

const titleMap = {
  1: "매장 홍보",
  2: "상품 소개",
  3: "이벤트",
};

// 필요하면 실제 값에 맞게 수정
const ageMap = {
  10: "10대",
  20: "20대",
  30: "30대",
  40: "40대",
  50: "50대",
  60: "60대 이상",
};

const styleMap = {
  1: "3D감성",
  2: "포토실사",
  3: "캐릭터/만화",
  4: "레트로",
  5: "AI모델",
  6: "예술",
  7: "원본 사용",
  8: "배경만 제거",
  9: "배경 AI변경",
  10: "필터"
};

const isTwoButtonStyle = (style) => style >= 1 && style <= 6;

const MyCalendarDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rec = location.state?.record;

  const { headerRef, footerRef, headerSpace, bottomSpace } = useFixedLayout();
  const { fullDate, onlyDate, onlyTime } = useFormattedDateOnce();
  const [, setSharePending] = useState(false);
  const [, setIsLoading] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenAll, setTokenAll] = useState(0);

  useEffect(() => {
    const fetchToken = async () => {
      const userId = localStorage.getItem("user_id");
      const res = await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/get/user/info`, { userId });
      setTokenAll(Number(res.data?.ticket_info?.token_amount ?? 0));
    };

    fetchToken();
  }, []);

  // 팝업창
  const [popupOpen, setPopupOpen] = useState(false);
  const onOut = () => {
    setPopupOpen(false);
  }
  const [noPopup, setNoPopup] = useState(false); // 기본: 체크 ON
  const NoPopup = () => {
    setNoPopup((prev) => !prev);
  };

  const [skipRegenPopup, setSkipRegenPopup] = useState(() => {
    try {
      const v = localStorage.getItem("re_popup");
      // 서버에서 bool → localStorage 저장 시 "true" / "false" 문자열이라고 가정
      return v === "true";
    } catch {
      return false;
    }
  });  

  // rec 없어도 안전하게 텍스트 준비
  const rawCopyright = rec?.copyright ?? "";
  const cleanCopyright = rawCopyright.replace(/^["']+|["']+$/g, "");
  const instaText = rec?.insta_copyright || "";

  // 제목/내용 분리 훅 (이벤트일 때 내용만 쓰려고)
  const { editBotText } = useTextExtraction(cleanCopyright);

  // 직접 URL로 들어오면 뒤로 보내기
  useEffect(() => {
    if (!rec) navigate(-1);
  }, [rec, navigate]);

  if (!rec) return null;

  const channelName = channelMap[rec.channel] || "";
  const channelIcon = channelIconMap[channelName];
  const titleLabel = titleMap[rec.title] || "";
  const ageLabel = ageMap[rec.age] || rec.age || "-";
  const styleLabel = styleMap[rec.style] || "-";
  const displayCopyright = titleLabel === "이벤트" ? editBotText : cleanCopyright;
  const dateLabel = dayjs(rec.start_date).isValid()
    ? dayjs(rec.start_date).format("YYYY.MM.DD")
    : "";
  
  // 업로드
  const onUpload = async () => {
    setIsLoading(true);

    const imageData = rec.image_path;
    if (!imageData) {
      alert("이미지 경로가 없습니다.");
      setIsLoading(false);
      return;
    }

    // 2) /ads/auto/app/save 로 새 기록 저장 (ManualMarketingAIResult 패턴 그대로)
    const manualDateRange = [onlyDate, onlyDate];
    const userId = localStorage.getItem("user_id");

    // age 포맷 맞추기: 3 -> "30"
    let ageForSave;
    if (rec.age >= 1 && rec.age <= 6) {
      ageForSave = String(rec.age) + "0";
    } else {
      ageForSave = String(rec.age ?? "");
    }

    const data = {
      title: String(rec.title),
      age: ageForSave,
      style: String(rec.style),
      channel: String(rec.channel),
      repeat: "없음",
      date_range: manualDateRange,
      upload_time: onlyTime,
      alert_check: false,
      user_id: userId,
      image: imageData,
      type: rec.type || "manual",
      prompt: rec.prompt || "",
      insta_copyright: instaText || "",
      copyright: rec.copyright || "",
    };

    const response = await axios.post(
      `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/auto/app/save`,
      data,
      { headers: { "Content-Type": "application/json" } }
    );

    const { success, imageUrl } = response.data || {};
    if (!success || !imageUrl) {
      alert("저장 실패");
      setIsLoading(false);
      return;
    }

    // 3) 저장된 새 imageUrl 기준으로 다시 포스팅
    const requestId = Math.random().toString(36).slice(2);
    const platform = platformFromChannel(rec.channel);
    let link = "";

    if (platform === "KAKAO") {
      const name = (localStorage.getItem("storeName") || "").trim();
      const region = (localStorage.getItem("roadName") || "").trim();
      if (name && region) {
        const label = `${region} ${name}`.trim();
        const long = `https://map.naver.com/p/search/${encodeURIComponent(
          label
        )}`;
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/g`,
            { url: long }
          );
          link = res.data?.short || "";
        } catch (e) {
          console.error("단축 링크 생성 실패:", e);
        }
      }
    }

    setSharePending(true);
    postToApp({
      type: "share.toChannel",
      requestId,
      social: platform,
      data: {
        imageUrl,
        caption: instaText || "",
        couponEnabled: false,
        ...(platform === "KAKAO" && link && { link }),
      },
    });

    // 4) 웹(dev) 환경이면 여기서 바로 끝
    if (!window.ReactNativeWebView) {
      // console.log("[DEV] START_SHARE", {
      //   platform,
      //   caption: instaText,
      //   imageUrl: imageUrl.slice(0, 64) + "...",
      //   time: fullDate,
      // });

      navigate("/manual/marketing/final", {
        state: {
          image: imageUrl,
          selections: {
            title: rec.title,
            age: rec.age,
            channel: rec.channel,
            style: rec.style,
          },
          instaText: instaText || "",
          storeBusinessNumber: rec.store_business_number,
          type: "manual",
          time: fullDate,
        },
      });
    }

    // 5) 앱에서 SHARE_RESULT 수신
    const off = addAppMessageListener((msg) => {
      if (msg?.type !== "SHARE_RESULT") return;
      if (msg?.payload?.platform && msg.payload.platform !== platform)
        return;

      off?.();
      setSharePending(false);
      setIsLoading(false);

      const ok = msg?.payload?.success === true;
      if (!ok) {
        alert(
          "해당 앱이 존재하지 않습니다. 앱 설치 후 다시 시도해주세요."
        );
        return;
      }

      navigate("/manual/marketing/final", {
        state: {
          image: imageUrl,
          selections: {
            title: rec.title,
            age: rec.age,
            channel: rec.channel,
            style: rec.style,
          },
          instaText: instaText || "",
          storeBusinessNumber: rec.store_business_number,
          type: rec.type || "manual",
          time: fullDate,
        },
      });
    });

    setTimeout(() => {
      off?.();
      setSharePending(false);
      setIsLoading(false);
    }, 150000);
  };

  const handleRegenerate = () => {
    if (tokenAll <= 0) {
      setShowTokenModal(true);
      return;
    }

    if (!rec) {
      alert("다시 생성에 필요한 데이터가 없습니다.");
      return;
    }

    const manualDataFromState = location.state?.manualData;

    const customMenu = manualDataFromState?.customMenu || localStorage.getItem("customMenu") || "";
    
    // 채널 코드(숫자) → 백엔드에서 사용하는 문자열로 변환
    const channelForManual =
      rec.channel === 1
      ? "카카오톡"
      : rec.channel === 2
      ? "인스타 스토리"
      : rec.channel === 3
      ? "인스타 게시물"
      : rec.channel === 6
      ? "네이버 밴드" // 🔥 백엔드 if문이 "네이버밴드"로 비교
      : ""; // 혹시 모를 기본값
    let subChannel = "";
    if (rec.channel === 2) subChannel = "스토리";
    else if (rec.channel === 3) subChannel = "피드";
    
    // const ageCode = rec.age ? String(rec.age)[0] : "";
    const ageLabel = ageMap[Number(rec.age)] || "";
    const themeLabel = titleMap[Number(rec.title)] || "";

    const manualData = manualDataFromState || {
      store_business_number: localStorage.getItem("store_business_number"),
      main: rec.main ?? "",
      temp: rec.temp ?? 0.7,
      style: rec.style,
      category: rec.category ?? null,
      age: ageLabel,
      channel: channelForManual,
      subChannel: subChannel,
      customText: displayCopyright,
      ad_text: displayCopyright,
      ad_text_override: displayCopyright,
      ad_text_theme: themeLabel,
      customMenu,
      theme: themeLabel,
      store_name: rec.store_name ?? (localStorage.getItem("storeName") || ""),
      road_name: rec.road_name ?? (localStorage.getItem("roadName") || ""),
      district_name: rec.district_name ?? "",
      detail_category_name: rec.detail_category_name ?? "",
      prompt: rec.prompt ?? "",
      type: rec.type || "manual",
    };

    // console.log("myCalendarDatail에서의 manualData", manualData)
    navigate("/manual/marketing/ai/loading", {
      state: manualData,
    });
  };

  const handleRegenClick = async () => {
    const userId = localStorage.getItem("user_id");

    // ✅ 다시 보지 않기 체크 시: 서버 + localStorage 저장
    if (noPopup && !skipRegenPopup && userId) {
      try {
        await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/re_popup`,
          { user_id: userId, re_popup: true }
        );
      } catch (err) {
        console.log("re_popup save error", err);
      }

      localStorage.setItem("re_popup", "true");
      setSkipRegenPopup(true);
    }

    setPopupOpen(false);
    handleRegenerate(); // ✅ 실제 재생성 진행
  };

  return (
    <div className="h-dvh">
      {/* 상단 헤더 */}
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
        <HeaderIconL
          title=""
          icon={left}
          onIconClick={() => navigate(-1)}          
        />
      </div>

      <main className="px-[16px] pb-[24px] flex flex-col gap-[16px]"
        style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}>
        {/* 카드 1: 채널 + 이미지 */}
        <section className="w-full rounded-[12px] bg-surface-light-subtle border border-border-subtle px-[16px] pt-[12px] justify-center">
          {/* 상단 채널 정보 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[8px]">
              {channelIcon && (
                <img
                  src={channelIcon}
                  alt={channelName}
                  className="w-[24px] h-[24px]"
                />
              )}
              <div className="flex items-center gap-[4px]">
                <span className="text-t4 text-text-subtle">{channelName}</span>
                <span className="text-b2 text-text-subtler">・</span>
                {dateLabel && (
                  <span className="text-b2 text-text-subtle">{dateLabel}</span>
                )}
              </div>
            </div>
          </div>

          {/* 가운데 정렬 이미지 */}
          <div className="w-full flex flex-col items-center pt-[24px]">
            <div className={`
              overflow-hidden bg-surface-light-subtler
              ${[1, 2].includes(rec.channel)
                ? "w-[180px] rounded-[12px] mb-[24px]"
                : "w-[200px] rounded-[16px] mb-[16px]"}
            `}>
              <img
                src={rec.image_path}
                alt={titleLabel}
                className="w-full h-full object-contain block"
              />
            </div>
            {instaText && (
                <p className="pb-[16px] text-c2 text-text-subtler">
                  {instaText}
                </p>
            )}
          </div>
        </section>

        {/* 카드 2: 옵션 정보 */}
        <section className="w-full bg-surface-light-white pt-[32px]">
            <h2 className="text-t4 text-text-subtle pb-[8px]">옵션 정보</h2>

            {/* 옵션 내용 박스 */}
            <div className="rounded-[12px] border border-border-subtle overflow-hidden">
                {/* 행 1: 고객 */}
                <div className="flex items-center justify-between p-[12px] gap-[12px]">
                  <span className="text-c2 text-text-subtler">고객</span>
                  <span className="text-c1 text-text-subtle">
                      {ageLabel}
                  </span>
                </div>

                {/* 행 2: 채널 */}
                <div className="flex items-center justify-between p-[12px] gap-[12px] border-t border-border-subtle">
                  <span className="text-c2 text-text-subtler">채널</span>
                  <span className="text-c1 text-text-subtle">
                      {channelName}
                  </span>
                </div>

                {/* 행 3: 맞춤 주제 + 아래 한 줄 문구 */}
                <div className="p-[12px] gap-[12px] border-t border-border-subtle">
                  <div className="flex items-center justify-between">
                      <span className="text-c2 text-text-subtler">맞춤 주제</span>
                      <span className="text-c1 text-text-subtle">
                        {titleLabel}
                      </span>
                  </div>
                  <div className="text-c1 text-text-subtle pt-[4px]">{displayCopyright}</div>
                </div>

                {/* 행 4: 이미지 스타일 */}
                <div className="flex items-center justify-between p-[12px] gap-[12px] border-t border-border-subtle">
                <span className="text-c2 text-text-subtler">이미지 스타일</span>
                <span className="text-c1 text-text-subtle">
                    {styleLabel}
                </span>
                </div>
            </div>
        </section>
      </main>

      <div ref={footerRef} className="fixed left-0 right-0 bottom-0 bg-white">
        {isTwoButtonStyle(rec.style) ? (
          <SplitRowHalf
            leftText="다시 포스팅하기"
            leftType="border"
            onLeft={onUpload}
            rightText="다시 생성하기"
            onRight={() => {
              if (skipRegenPopup) handleRegenerate();
              else setPopupOpen(true);
            }}              
            rightRightIcon={retry}
          />
        ) : (
          <FillButton
            onClick={onUpload}
          >
            다시 포스팅하기
          </FillButton>
        )}
      </div>

      <Popup
        isOpen={popupOpen}
        onClose={onOut}
        title="다시 생성하기"
        subtitle={
          <>
            <span className="text-text-danger">
              * 이미지를 재생성할 경우 토큰이 차감됩니다.
            </span>
          </>
        }

        renderButtons={
          <>
            <button
              type="button"
              className="flex items-center p-[24px] pb-[16px]"
              onClick={NoPopup}
            >
              <img
                src={noPopup ? checkOn : checkOff}
                alt=""
                className="pr-[8px]"
              />
              <span
                className={`text-b1 ${noPopup ? "text-text-default" : "text-text-disabled"}`}
              >
                다시 보지 않기
              </span>
            </button>
            <TwinColumn
              topType={"fill"}
              topText={"다시 생성하기"}
              onTop={handleRegenClick}
              bottomType={"empty"}
              bottomText={"취소"}
              onBottom={onOut}
            />
          </>
          }
      />
      <TokenPopup
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        origin="myCalendarDetail"
        returnTo="/mycalendar/detail"
        returnState={{ ...location.state }}
      />
    </div>
  );
};

export default MyCalendarDetail;
